/**
 * CreateEventLandingStep — the wizard's Landing step (create.landing).
 *
 * Event-level model: one event → MANY landing pages, each scoped to a subset of
 * the event's ticket types and/or ticket groups. This step manages that list
 * (add / edit / delete / cover which ticket types/groups) and delegates the
 * actual page design to CreateEventLanding in `embedded` mode (scoped to the
 * page's targets).
 *
 * Persistence: pages live in the creation config (`cfg.landingPages`), which is
 * saved with the in-progress draft. The FIRST page (`lp_main`, covers all the
 * event's ticket types + groups) mirrors the singular `cfg.slug` /
 * `cfg.landingDesign` that Review & Publish already reads, so the publish path is
 * unchanged. The scoped pages persist through the declared multi-landing-page
 * ops at wiring time — see the changelog's data-layer notes.
 */
import { useEffect, useMemo, useState } from "react";
import { Btn, Card, Icon, IconBtn, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { useNav } from "../../shell/router";
import { CreationShell } from "../events/creation/CreationShell";
import { CreationStore, useCreationConfig, wizardNeighbors } from "../events/creation/store";
import { WizardCanvas } from "../events/creation/wizard-utils";
import { useTicketTypes, type TicketType } from "../tickets/ticket-types-store";
import { useTicketGroups, type TicketGroup } from "../tickets/ticket-groups-store";
import { publicHost } from "../../lib/public-url";
import { CreateEventLanding, defaultLandingBlocks, type EventLandingPage, type EmbeddedLandingSave } from "./LandingBuilder";
import { useT, t as i18nT } from "../../lib/i18n";

const MAIN_ID = "lp_main";
const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const uid = () => `lp_${Date.now().toString(36)}${((Math.random() * 1e4) | 0).toString(36)}`;

export default function CreateEventLandingStep() {
  const t = useT();
  const nav = useNav();
  const cfg = useCreationConfig();
  const allTypes = useTicketTypes();
  const allGroups = useTicketGroups();
  const hostPrefix = publicHost("attend");

  const eventTypeIds = useMemo(() => ((cfg.ticketTypeIds as string[] | undefined) || []), [cfg]);
  const eventGroupIds = useMemo(() => ((cfg.ticketGroupIds as string[] | undefined) || []), [cfg]);
  const eventTypes = useMemo(() => allTypes.filter((t) => eventTypeIds.includes(t.id)), [allTypes, eventTypeIds]);
  const eventGroups = useMemo(() => allGroups.filter((g) => eventGroupIds.includes(g.id)), [allGroups, eventGroupIds]);
  const typeById = useMemo(() => Object.fromEntries(allTypes.map((t) => [t.id, t])), [allTypes]);
  const groupById = useMemo(() => Object.fromEntries(allGroups.map((g) => [g.id, g])), [allGroups]);
  const hasTargets = eventTypeIds.length > 0 || eventGroupIds.length > 0;

  // A ticket group targets its tiers; resolve to the tier ids that appear as
  // rows in cfg.tickets so the builder can scope its preview/registration.
  const tierIdsForGroups = (ids: string[]) => ids.flatMap((id) => (groupById[id]?.tiers || []).map((t) => t.id));

  const pages = ((cfg.landingPages as EventLandingPage[] | undefined) || []);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  // Seed the primary page once, mirroring the singular landingDesign/slug that
  // Publish reads, so this step is backward-compatible with existing events.
  useEffect(() => {
    if (pages.length) return;
    const ld = cfg.landingDesign as { blocks?: EventLandingPage["design"]["blocks"]; theme?: string; font?: string } | undefined;
    const title = String(cfg.title || "Main page");
    const main: EventLandingPage = {
      id: MAIN_ID,
      name: title,
      slug: String(cfg.slug || slugify(title) || "event"),
      ticketTypeIds: eventTypeIds,
      ticketGroupIds: eventGroupIds,
      design: {
        blocks: ld?.blocks?.length ? ld.blocks : defaultLandingBlocks(cfg as Record<string, unknown>, title),
        theme: ld?.theme || "brand",
        font: ld?.font || "DM Sans",
      },
      status: "draft",
    };
    CreationStore.setField("landingPages", [main]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the primary page's ticket coverage in sync with the event's selection
  // (it always covers every ticket type/group not claimed by a specific page).
  const claimedTypes = useMemo(() => new Set(pages.filter((p) => p.id !== MAIN_ID).flatMap((p) => p.ticketTypeIds)), [pages]);
  const claimedGroups = useMemo(() => new Set(pages.filter((p) => p.id !== MAIN_ID).flatMap((p) => p.ticketGroupIds)), [pages]);

  const scopeFor = (p: EventLandingPage): string[] => {
    const typeIds = p.id === MAIN_ID ? eventTypeIds : p.ticketTypeIds;
    const grpIds = p.id === MAIN_ID ? eventGroupIds : p.ticketGroupIds;
    return [...typeIds, ...tierIdsForGroups(grpIds)];
  };

  const savePage = (patch: EmbeddedLandingSave) => {
    const existing = pages.find((x) => x.id === patch.id);
    const merged: EventLandingPage = existing
      ? { ...existing, name: patch.name, slug: patch.slug, design: patch.design, status: patch.status }
      : { ...patch, ticketTypeIds: [], ticketGroupIds: [] };
    const next = existing ? pages.map((x) => (x.id === patch.id ? merged : x)) : [...pages, merged];
    CreationStore.setField("landingPages", next);
    if (patch.id === MAIN_ID) {
      CreationStore.setField("slug", patch.slug);
      CreationStore.setField("landingDesign", patch.design);
    }
  };
  const addPage = (p: EventLandingPage) => { CreationStore.setField("landingPages", [...pages, p]); };
  const deletePage = (id: string) => {
    if (id === MAIN_ID) return;
    CreationStore.setField("landingPages", pages.filter((p) => p.id !== id));
  };

  // ── Embedded page editor ──────────────────────────────────────────────
  if (editingId) {
    const page = pages.find((p) => p.id === editingId);
    if (page) {
      return (
        <CreateEventLanding
          standalone
          embedded
          key={page.id}
          scopeTicketTypeIds={scopeFor(page)}
          pageMeta={{ id: page.id, title: page.name, slug: page.slug, status: page.status }}
          initialPage={{ id: page.id, blocks: page.design.blocks, theme: page.design.theme, font: page.design.font, event: null }}
          onSavePage={savePage}
          onBack={() => setEditingId(null)}
        />
      );
    }
  }

  const footer = (
    <>
      <Btn variant="secondary" icon={<Icon.arrow dir="left" size={14} />} onClick={() => nav.go(wizardNeighbors(cfg as Record<string, unknown>, "create.landing").back || "create.registration")}>{t("common.actions.back", "Back")}</Btn>
      <Btn iconRight={<Icon.arrow size={14} />} onClick={() => nav.go(wizardNeighbors(cfg as Record<string, unknown>, "create.landing").next || "create.publish")}>{t("common.actions.continue", "Continue")}</Btn>
    </>
  );

  const chip = (t: TicketType) => (
    <span key={t.id} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 999, background: alpha(t.color, "18"), border: `1px solid ${alpha(t.color, "44")}`, fontSize: 12, fontWeight: 600, color: T.n1 }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.color }} />
      {t.name || "Ticket type"}
    </span>
  );
  const groupChip = (g: TicketGroup) => (
    <span key={g.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 9px", borderRadius: 999, background: T.n6, border: `1px solid ${T.n4}`, fontSize: 12, fontWeight: 600, color: T.n1 }}>
      <span style={{ display: "inline-flex", width: 20, height: 5, borderRadius: 999, overflow: "hidden" }}>{(g.tiers || []).map((ti) => <span key={ti.id} style={{ flex: 1, background: ti.color }} />)}</span>
      {g.name || "Ticket group"}
    </span>
  );

  const uncoveredTypes = eventTypes.filter((t) => !claimedTypes.has(t.id));
  const uncoveredGroups = eventGroups.filter((g) => !claimedGroups.has(g.id));
  const uncoveredLabel = [...uncoveredTypes.map((t) => t.name), ...uncoveredGroups.map((g) => g.name)].filter(Boolean).join(", ");

  return (
    <CreationShell step={5} footer={footer}>
      <WizardCanvas width="wide" cols="stack">
        <div style={{ padding: "12px 16px", background: T.p4, border: `1px solid ${alpha(T.p1, "22")}`, borderRadius: 10, display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: T.n2, lineHeight: 1.5 }}>
          <Icon.info size={15} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>{t("landing.stepManager.designOneOrMorePublicPagesForThisEventEachPageHas", "Design one or more public pages for this event. Each page has its own URL and can target specific")}{" "}<b style={{ color: T.n1 }}>{t("landing.stepManager.ticketTypesOrTicketGroups", "ticket types or ticket groups")}</b> {t("landing.stepManager.eGAVipPageAndAGeneralAdmissionPageThe", "— e.g. a VIP page and a general-admission page. The")}{" "}<b style={{ color: T.n1 }}>{t("landing.stepManager.mainPage", "Main page")}</b> {t("landing.stepManager.coversEverything", "covers everything.")}</span>
        </div>

        {!hasTargets ? (
          <Card style={{ padding: 22, display: "flex", flexDirection: "column", gap: 10, alignItems: "center", textAlign: "center" }}>
            <span style={{ fontSize: 13.5, color: T.soft }}>{t("landing.stepManager.noTicketTypesOrGroupsAreSelectedForThisEventYetPages", "No ticket types or groups are selected for this event yet — pages can still be built, but targeting needs at least one ticket type or group.")}</span>
            <Btn variant="secondary" size="sm" icon={<Icon.arrow dir="left" size={13} />} onClick={() => nav.go("create.tickets")}>{t("landing.stepManager.backToTickets", "Back to Tickets")}</Btn>
          </Card>
        ) : null}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {pages.map((p) => {
            const isMain = p.id === MAIN_ID;
            const covered = (isMain ? eventTypeIds : p.ticketTypeIds).map((id) => typeById[id]).filter(Boolean) as TicketType[];
            const coveredGroups = (isMain ? eventGroupIds : p.ticketGroupIds).map((id) => groupById[id]).filter(Boolean) as TicketGroup[];
            const totalCovered = isMain ? (eventTypeIds.length + eventGroupIds.length) : (p.ticketTypeIds.length + p.ticketGroupIds.length);
            return (
              <Card key={p.id} style={{ padding: 0, overflow: "hidden", display: "flex" }}>
                <div style={{ width: 6, background: `linear-gradient(180deg, ${covered[0]?.color || coveredGroups[0]?.tiers?.[0]?.color || T.p1}, ${T.p1})`, flexShrink: 0 }} />
                <div style={{ flex: 1, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 15.5, fontWeight: 700, color: T.n1 }}>{p.name || "Untitled page"}</span>
                    {isMain ? <Pill tone="primary">{t("landing.stepManager.mainPage", "Main page")}</Pill> : null}
                    <span style={{ fontFamily: T.fontMono, fontSize: 12, color: T.p1 }}>{hostPrefix}/{p.slug}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    {isMain ? (
                      <span style={{ fontSize: 12.5, color: T.soft }}>{t("landing.stepManager.coversAll", "Covers all")}{" "}{totalCovered} {t("landing.stepManager.ticketType", "ticket type")}{" "}{totalCovered === 1 ? "" : "s"} {i18nT("landing.stepManager.ampGroup.rendered", "& group")}{eventGroupIds.length === 1 ? "" : "s"}</span>
                    ) : totalCovered ? <>{covered.map(chip)}{coveredGroups.map(groupChip)}</> : (
                      <span style={{ fontSize: 12.5, color: T.soft }}>{t("landing.stepManager.nothingAssignedYet", "Nothing assigned yet")}</span>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 16px", flexShrink: 0 }}>
                  <Btn variant="secondary" size="sm" icon={<Icon.edit size={13} />} onClick={() => setEditingId(p.id)}>{t("landing.stepManager.editPage", "Edit page")}</Btn>
                  {!isMain ? <IconBtn label={t("landing.stepManager.deletePage", "Delete page")} onClick={() => deletePage(p.id)}><Icon.trash size={14} /></IconBtn> : null}
                </div>
              </Card>
            );
          })}
        </div>

        <button type="button" onClick={() => setAdding(true)} disabled={!hasTargets}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "16px", borderRadius: 10, border: `2px dashed ${T.n4}`, background: "transparent", color: !hasTargets ? T.n4 : T.p1, fontWeight: 600, fontSize: 13.5, fontFamily: T.font, cursor: !hasTargets ? "not-allowed" : "pointer", width: "100%" }}>
          <Icon.plus size={15} /> {t("landing.stepManager.addALandingPageForSpecificTicketTypesOrGroups", "Add a landing page for specific ticket types or groups")}</button>

        {uncoveredLabel && pages.length ? (
          <div style={{ fontSize: 12.5, color: T.soft, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <Icon.info size={13} /> {t("landing.stepManager.notOnADedicatedPageServedByTheMainPage", "Not on a dedicated page (served by the Main page):")}{" "}{uncoveredLabel}
          </div>
        ) : null}
      </WizardCanvas>

      {adding ? (
        <AddPageDialog
          eventTypes={eventTypes}
          eventGroups={eventGroups}
          disabledTypeIds={claimedTypes}
          disabledGroupIds={claimedGroups}
          onClose={() => setAdding(false)}
          onCreate={(name, ticketTypeIds, ticketGroupIds) => {
            const id = uid();
            const page: EventLandingPage = {
              id, name, slug: slugify(name) || id,
              ticketTypeIds,
              ticketGroupIds,
              design: { blocks: defaultLandingBlocks(cfg as Record<string, unknown>, name), theme: "brand", font: "DM Sans" },
              status: "draft",
            };
            addPage(page);
            setAdding(false);
            setEditingId(id);
          }}
        />
      ) : null}
    </CreationShell>
  );
}

function AddPageDialog({ eventTypes, eventGroups, disabledTypeIds, disabledGroupIds, onClose, onCreate }: { eventTypes: TicketType[]; eventGroups: TicketGroup[]; disabledTypeIds: Set<string>; disabledGroupIds: Set<string>; onClose: () => void; onCreate: (name: string, ticketTypeIds: string[], ticketGroupIds: string[]) => void }) {
  const t = useT();
  const translate = t;
  const [name, setName] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [pickedGroups, setPickedGroups] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setPicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleGroup = (id: string) => setPickedGroups((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const canCreate = name.trim().length > 0 && (picked.size + pickedGroups.size) > 0;
  const input: React.CSSProperties = { width: "100%", height: 40, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, fontSize: 14, fontFamily: T.font, color: T.n1, background: T.white, boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(51,51,51,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 560, maxHeight: "82vh", background: T.white, borderRadius: 12, boxShadow: T.shadowLg, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}` }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: T.n1, margin: 0 }}>{t("landing.stepManager.newLandingPage", "New landing page")}</h3>
          <p style={{ fontSize: 13, color: T.soft, margin: "4px 0 0" }}>{t("landing.stepManager.nameThePageAndChooseWhichTicketTypesOrGroupsItTargets", "Name the page and choose which ticket types or groups it targets. Each can belong to one dedicated page.")}</p>
        </div>
        <div style={{ overflow: "auto", padding: "18px 24px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n1, marginBottom: 6 }}>{t("landing.stepManager.pageName", "Page name")}</div>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t("landing.stepManager.eGVipExperience", "e.g. VIP experience")} style={input} />
          </div>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n1, marginBottom: 8 }}>{t("landing.stepManager.ticketTypesOnThisPage", "Ticket types on this page")}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {eventTypes.map((t) => {
                const on = picked.has(t.id);
                const claimed = disabledTypeIds.has(t.id);
                return (
                  <button key={t.id} type="button" disabled={claimed} onClick={() => toggle(t.id)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: `${on ? 2 : 1}px solid ${on ? T.p1 : T.n4}`, borderRadius: 9, background: on ? T.p4 : T.white, cursor: claimed ? "not-allowed" : "pointer", opacity: claimed ? 0.5 : 1, fontFamily: T.font, textAlign: "left", width: "100%" }}>
                    <span style={{ width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${on ? T.p1 : T.n4}`, background: on ? T.p1 : T.white, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{on ? <Icon.check size={12} /> : null}</span>
                    <span style={{ width: 9, height: 9, borderRadius: 999, background: t.color, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: T.n1 }}>{t.name || translate("eventday.eventDay.ticketType", "Ticket type")}</span>
                    {claimed ? <span style={{ fontSize: 11.5, color: T.soft }}>{translate("landing.stepManager.onAnotherPage", "On another page")}</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
          {eventGroups.length ? (
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n1, marginBottom: 8 }}>{t("landing.stepManager.ticketGroupsOnThisPage", "Ticket groups on this page")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {eventGroups.map((g) => {
                  const on = pickedGroups.has(g.id);
                  const claimed = disabledGroupIds.has(g.id);
                  return (
                    <button key={g.id} type="button" disabled={claimed} onClick={() => toggleGroup(g.id)}
                      style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: `${on ? 2 : 1}px solid ${on ? T.p1 : T.n4}`, borderRadius: 9, background: on ? T.p4 : T.white, cursor: claimed ? "not-allowed" : "pointer", opacity: claimed ? 0.5 : 1, fontFamily: T.font, textAlign: "left", width: "100%" }}>
                      <span style={{ width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${on ? T.p1 : T.n4}`, background: on ? T.p1 : T.white, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{on ? <Icon.check size={12} /> : null}</span>
                      <span style={{ display: "inline-flex", width: 24, height: 6, borderRadius: 999, overflow: "hidden", flexShrink: 0 }}>{(g.tiers || []).map((ti) => <span key={ti.id} style={{ flex: 1, background: ti.color }} />)}</span>
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: T.n1 }}>{g.name || t("landing.landingStepManager.ticketGroup", "Ticket group")}</span>
                      <span style={{ fontSize: 11.5, color: T.soft }}>{claimed ? t("landing.stepManager.onAnotherPage", "On another page") : t("landing.landingStepManager.tierCount.icu", { count: g.tiers.length })}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
        <div style={{ padding: "14px 24px", borderTop: `1px solid ${T.border}`, background: T.n6, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
          <Btn icon={<Icon.plus size={13} />} disabled={!canCreate} onClick={() => onCreate(name.trim(), [...picked], [...pickedGroups])}>{i18nT("landing.stepManager.createAmpDesign.rendered", "Create & design")}</Btn>
        </div>
      </div>
    </div>
  );
}
